'use client';

import type { PaymentMethodDataEncCardForWorldpay } from '@payments/aurum-contracts';
import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useEffect, useRef, useState } from 'react';

function useWorldpayCardSessionId() {
  const debug = createDebug({ isEnabled: false });
  const { isProduction } = useClientConfig();
  const [idempotencyToken, setIdempotencyToken] = useState<string | null>(null);
  const iFrameElementRef = useRef<HTMLIFrameElement | null>(null);
  const jwtRef = useRef<string | null>(null);
  const WORLD_PAY_GET_SESSION_ID_TIMEOUT_MS = 15000;
  const JWT_POLLING_TIMEOUT_MS = 15000;

  // Track active timeouts and intervals for cleanup
  const activeTimeouts = useRef<Set<NodeJS.Timeout>>(new Set());
  const activeIntervals = useRef<Set<NodeJS.Timeout>>(new Set());

  const JWT_URL = isProduction ? API_ROUTES.PAYMENTS.WORLDPAY.JWT_PROD_URL : API_ROUTES.PAYMENTS.WORLDPAY.JWT_TEST_URL;

  const IFRAME_ORIGIN_TEST = 'https://centinelapistag.cardinalcommerce.com';
  const IFRAME_ORIGIN_PROD = 'https://centinelapi.cardinalcommerce.com';

  const IFRAME_ID = 'worldPayIframe';

  const getIframeOrigin = () => {
    return isProduction ? IFRAME_ORIGIN_PROD : IFRAME_ORIGIN_TEST;
  };

  const { data: jwt, isLoading: isJwtLoading } = useApiQuery<string>(JWT_URL, {
    credentials: false,
    responseType: 'text',
    params: idempotencyToken ? { idempotencyToken } : undefined,
    headers: {
      apiKey: '',
    },
    queryOptions: {
      enabled: !!idempotencyToken,
      retry: 2,
    },
  });

  // Update ref when JWT changes
  useEffect(() => {
    jwtRef.current = jwt || null;
  }, [jwt]);

  const getJwt = (idempotencyToken: string): Promise<string> => {
    setIdempotencyToken(idempotencyToken);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        activeTimeouts.current.delete(timeoutId);
        activeIntervals.current.delete(intervalId);
        reject(new Error('Timeout: Failed to get Worldpay JWT'));
      }, JWT_POLLING_TIMEOUT_MS);

      const intervalId = setInterval(() => {
        const currentJwt = jwtRef.current;

        if (!isJwtLoading && currentJwt) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          activeTimeouts.current.delete(timeoutId);
          activeIntervals.current.delete(intervalId);
          resolve(currentJwt);
        }
      }, 100);

      // Track timeouts and intervals for cleanup
      activeTimeouts.current.add(timeoutId);
      activeIntervals.current.add(intervalId);
    });
  };

  const getIFrameDocument = () => {
    return iFrameElementRef.current?.contentDocument || iFrameElementRef.current?.contentWindow?.document;
  };

  const setupIframe = (bin: string, jwt: string) => {
    const iframe = document.createElement('iframe') as HTMLIFrameElement;
    iFrameElementRef.current = iframe;

    iframe.style.display = 'none';
    iframe.id = IFRAME_ID;
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms');

    document.body.appendChild(iframe);

    const iFrameDocument = iframe.contentDocument || iframe.contentWindow?.document;

    if (!iFrameDocument) {
      debug.forceError('Failed to get document from Worldpay iframe.');
      throw new Error();
    }
    iFrameDocument.open();
    iFrameDocument.write(getIframeContent(bin, jwt));
    iFrameDocument.close();
  };

  const submitIFrameForm = () => {
    const iFrameDocument = getIFrameDocument();
    const iFrameForm = iFrameDocument?.querySelector<HTMLFormElement>('form');

    if (!iFrameForm) {
      debug.forceError(`Failed to submit form in Worldpay iframe.`);
      throw new Error();
    }

    iFrameForm.submit();
  };

  const removeIframe = () => {
    document.querySelector(`#${IFRAME_ID}`)?.remove();
    iFrameElementRef.current = null;
  };

  const getIframeContent = (bin: string, jwt: string) => {
    return `
        <form
            method="POST"
            action="${getIframeOrigin()}/V1/Cruise/Collect"
        >
            <input type="hidden" name="Bin" value="${bin}" />
            <input type="hidden" name="JWT" value="${jwt}" />
        </form>
    `;
  };

  const getSessionId = async (bin: string, idempotencyToken: string): Promise<PaymentMethodDataEncCardForWorldpay['sessionId']> => {
    const iframeOrigin = getIframeOrigin();
    const jwt = await getJwt(idempotencyToken);

    setupIframe(bin, jwt);

    return new Promise(resolve => {
      let promiseHasFinished = false;
      const messageEventHandler = (event: MessageEvent) => {
        if (event.origin !== iframeOrigin) return;
        if (promiseHasFinished) return;
        promiseHasFinished = true;
        clearTimeout(sessionTimeoutId);
        activeTimeouts.current.delete(sessionTimeoutId);
        resolve(JSON.parse(event.data).SessionId);
        window.removeEventListener('message', messageEventHandler);
        removeIframe();
      };

      const sessionTimeoutId = setTimeout(() => {
        if (promiseHasFinished) return;
        promiseHasFinished = true;
        activeTimeouts.current.delete(sessionTimeoutId);
        // If we timeout on getting the session id from WorldPay's message, we resolve with an empty string,
        // and the backend will handle this case to make the payment succeed anyway.
        resolve('');
        window.removeEventListener('message', messageEventHandler);
        removeIframe();
      }, WORLD_PAY_GET_SESSION_ID_TIMEOUT_MS);

      // Track the session timeout for cleanup
      activeTimeouts.current.add(sessionTimeoutId);

      window.addEventListener('message', messageEventHandler);
      submitIFrameForm();
    });
  };

  /**
   * cleans up any active timeouts, intervals, and iframes
   */
  const cleanup = () => {
    // Clear all active timeouts
    activeTimeouts.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    activeTimeouts.current.clear();

    // Clear all active intervals
    activeIntervals.current.forEach(intervalId => {
      clearInterval(intervalId);
    });
    activeIntervals.current.clear();

    // Remove any existing iframe
    removeIframe();

    // Reset state
    setIdempotencyToken(null);
    jwtRef.current = null;
  };

  return {
    getSessionId,
    cleanup,
  };
}

export { useWorldpayCardSessionId };
